import { isArchivedLifecycle } from '@terra/shared-dto-types';
import {
  FINDINGS_GROUP_PUBLISHED_KEY,
  FINDINGS_SEVERITY_SORT_ORDER,
  FINDINGS_STATUS_SORT_ORDER,
  type FindingsGroupByField,
  type FindingsSortBy,
} from '@terra/shared-contracts-findings';
import { getFindingCvssDisplayValues } from '@terra/shared-cvss';
import { AssetTypeSchema } from '@terra/shared-schemas-orchestrator';
import {
  resolveClassificationGroupKey,
  resolveLinkedTicketGroupKey,
} from '@terra/shared-utils';
import {
  formatCategoryForDisplay,
  formatFindingStatusForDisplay,
} from '../../utils/category-utils';
import {
  buildGroupedRows,
  isGroupHeaderRow,
  type GroupHeaderRowBase,
} from '../grouping';
import type { EnrichedAsset, InternalEnrichedFinding } from '../../types';

export const FINDINGS_GROUP_HEADER_ROW_CLASS = 'findings-group-header-row';
export const FINDINGS_GROUP_HEADER_ID_PREFIX = '__findings_group__:';

type FindingsGroupableAsset = Pick<
  EnrichedAsset,
  'type' | 'id' | 'path' | 'url' | 'host' | 'port'
>;
type FindingsGroupableSignalSummary = Pick<
  NonNullable<InternalEnrichedFinding['signalSummary']>,
  'id' | 'title'
>;
type FindingsGroupableLifecycle = Pick<
  NonNullable<InternalEnrichedFinding['lifecycle']>,
  'state'
>;

export type FindingsGroupableFinding = Pick<
  InternalEnrichedFinding,
  | 'id'
  | 'severity'
  | 'status'
  | 'category'
  | 'title'
  | 'isPublished'
  | 'targetId'
  | 'cweIds'
  | 'owaspCategory'
> & {
  createdAt: InternalEnrichedFinding['createdAt'] | string;
  publishedAt?: InternalEnrichedFinding['publishedAt'] | string | null;
  discoveryDate?: InternalEnrichedFinding['discoveryDate'] | string | null;
  assets: readonly FindingsGroupableAsset[];
  externalReferences?: InternalEnrichedFinding['externalReferences'];
  signalId?: string | null;
  signalSummary?: FindingsGroupableSignalSummary | null;
  lifecycle?: FindingsGroupableLifecycle | null;
  targetName?: string | null;
  applicationName?: string | null;
  cvss?: InternalEnrichedFinding['cvss'] | null;
};
export type FindingsGroupHeaderRow = GroupHeaderRowBase & {
  findingIds: string[];
  bulkActionableFindingIds: string[];
};

export type FindingsGridRow = InternalEnrichedFinding | FindingsGroupHeaderRow;

export type FindingsGroupLabelResolvers = {
  emptyValue: string;
  published: string;
  unpublished: string;
  formatDateDay: (date: Date) => string;
};

export type FindingsGroupSort = {
  sortBy: FindingsSortBy;
  sortOrder: 'asc' | 'desc';
};

export const FINDINGS_GROUP_SORT: Record<
  FindingsGroupByField,
  FindingsGroupSort
> = {
  severity: { sortBy: 'severity', sortOrder: 'desc' },
  status: { sortBy: 'status', sortOrder: 'asc' },
  category: { sortBy: 'category', sortOrder: 'asc' },
  relatedEndpoint: { sortBy: 'endpointId', sortOrder: 'desc' },
  createdAt: { sortBy: 'createdAt', sortOrder: 'desc' },
  relatedSignal: { sortBy: 'signalId', sortOrder: 'desc' },
  title: { sortBy: 'title', sortOrder: 'asc' },
  isPublished: { sortBy: 'isPublished', sortOrder: 'desc' },
  targetName: { sortBy: 'targetName', sortOrder: 'asc' },
  publishedAt: { sortBy: 'publishedAt', sortOrder: 'desc' },
  linkedTicket: { sortBy: 'linkedTicket', sortOrder: 'desc' },
  discoveryDate: { sortBy: 'discoveryDate', sortOrder: 'desc' },
  classification: { sortBy: 'classification', sortOrder: 'asc' },
  cvss: { sortBy: 'cvss', sortOrder: 'desc' },
};

export const FINDINGS_GROUPINGS_WITHOUT_THEN_SORT: ReadonlySet<FindingsGroupByField> =
  new Set([
    'relatedEndpoint',
    'createdAt',
    'publishedAt',
    'linkedTicket',
    'discoveryDate',
    'classification',
    'cvss',
  ]);

const compareRankedGroupKeys = ({
  left,
  right,
  rankOrder,
  direction,
}: {
  left: string;
  right: string;
  rankOrder: readonly string[];
  direction: 1 | -1;
}): number => {
  const leftRank = rankOrder.findIndex((value) => value === left);
  const rightRank = rankOrder.findIndex((value) => value === right);
  const normalizedLeft = leftRank === -1 ? rankOrder.length : leftRank;
  const normalizedRight = rightRank === -1 ? rankOrder.length : rightRank;

  return (normalizedLeft - normalizedRight) * direction;
};

const compareFindingsGroupKeys = ({
  left,
  right,
  groupByField,
  entityLabelByKey,
}: {
  left: string;
  right: string;
  groupByField: FindingsGroupByField;
  entityLabelByKey?: ReadonlyMap<string, string>;
}): number => {
  if (left === right) {
    return 0;
  }

  if (left === '') {
    return 1;
  }

  if (right === '') {
    return -1;
  }

  const { sortOrder } = FINDINGS_GROUP_SORT[groupByField];
  const direction = sortOrder === 'desc' ? -1 : 1;

  if (groupByField === 'severity') {
    return compareRankedGroupKeys({
      left,
      right,
      rankOrder: FINDINGS_SEVERITY_SORT_ORDER,
      direction,
    });
  }

  if (groupByField === 'status') {
    return compareRankedGroupKeys({
      left,
      right,
      rankOrder: FINDINGS_STATUS_SORT_ORDER,
      direction,
    });
  }

  if (groupByField === 'isPublished') {
    if (left === FINDINGS_GROUP_PUBLISHED_KEY.PUBLISHED) {
      return -1;
    }

    if (right === FINDINGS_GROUP_PUBLISHED_KEY.PUBLISHED) {
      return 1;
    }
  }

  if (groupByField === 'cvss') {
    const leftScore = Number(left);
    const rightScore = Number(right);
    const normalizedLeft = Number.isFinite(leftScore) ? leftScore : 0;
    const normalizedRight = Number.isFinite(rightScore) ? rightScore : 0;

    return (normalizedLeft - normalizedRight) * direction;
  }

  if (groupByField === 'targetName') {
    const leftLabel = entityLabelByKey?.get(left) ?? left;
    const rightLabel = entityLabelByKey?.get(right) ?? right;

    return leftLabel.localeCompare(rightLabel) * direction;
  }

  return left.localeCompare(right) * direction;
};

export const isFindingsGroupHeaderRow = (
  row: unknown,
): row is FindingsGroupHeaderRow => isGroupHeaderRow(row);

const getPrimaryAsset = (
  finding: FindingsGroupableFinding,
): FindingsGroupableAsset | undefined =>
  finding.assets.find(
    (asset) => asset.type === AssetTypeSchema.enum.ENDPOINT,
  ) ??
  finding.assets.find((asset) => asset.type === AssetTypeSchema.enum.WEBPAGE) ??
  finding.assets.find(
    (asset) => asset.type === AssetTypeSchema.enum.DISCOVERED_ASSET,
  );

const getPrimaryAssetLabel = (asset: FindingsGroupableAsset): string => {
  if (asset.type === AssetTypeSchema.enum.DISCOVERED_ASSET) {
    const host = asset.host || asset.url || asset.id;

    return asset.port ? `${host}:${asset.port}` : host;
  }

  if (asset.type === AssetTypeSchema.enum.ENDPOINT) {
    return asset.path ?? asset.url ?? asset.id;
  }

  return asset.url ?? asset.id;
};

const toCalendarDayKey = (value: Date | string | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const FINDINGS_GROUP_KEY_BY_FIELD: Record<
  FindingsGroupByField,
  (finding: FindingsGroupableFinding) => string
> = {
  severity: (finding) => finding.severity,
  status: (finding) => finding.status,
  category: (finding) => finding.category || '',
  title: (finding) => finding.title?.trim() || '',
  isPublished: (finding) =>
    finding.isPublished
      ? FINDINGS_GROUP_PUBLISHED_KEY.PUBLISHED
      : FINDINGS_GROUP_PUBLISHED_KEY.UNPUBLISHED,
  relatedEndpoint: (finding) => getPrimaryAsset(finding)?.id ?? '',
  relatedSignal: (finding) =>
    finding.signalId ?? finding.signalSummary?.id ?? '',
  createdAt: (finding) => toCalendarDayKey(finding.createdAt),
  targetName: (finding) => finding.targetId ?? '',
  publishedAt: (finding) => toCalendarDayKey(finding.publishedAt),
  linkedTicket: (finding) =>
    resolveLinkedTicketGroupKey(finding.externalReferences ?? []),
  discoveryDate: (finding) => toCalendarDayKey(finding.discoveryDate),
  classification: (finding) =>
    resolveClassificationGroupKey({
      cweIds: finding.cweIds,
      owaspCategory: finding.owaspCategory,
    }),
  cvss: (finding) => getFindingCvssDisplayValues(finding.cvss).score ?? '',
};

const FINDINGS_ENTITY_LABEL_BY_FIELD: Partial<
  Record<
    FindingsGroupByField,
    (finding: FindingsGroupableFinding) => string | null
  >
> = {
  relatedEndpoint: (finding) => {
    const asset = getPrimaryAsset(finding);

    return asset ? getPrimaryAssetLabel(asset) : null;
  },
  relatedSignal: (finding) => finding.signalSummary?.title?.trim() || null,
  targetName: (finding) =>
    finding.targetName?.trim() ||
    finding.applicationName?.trim() ||
    finding.targetId ||
    null,
  linkedTicket: (finding) => {
    const groupKey = resolveLinkedTicketGroupKey(
      finding.externalReferences ?? [],
    );

    return groupKey || null;
  },
};

export const getFindingsGroupKey = ({
  finding,
  groupByField,
}: {
  finding: FindingsGroupableFinding;
  groupByField: FindingsGroupByField;
}): string => FINDINGS_GROUP_KEY_BY_FIELD[groupByField](finding);

export const getFindingsGroupLabel = ({
  groupKey,
  groupByField,
  labels,
  entityLabelByKey,
}: {
  groupKey: string;
  groupByField: FindingsGroupByField;
  labels: FindingsGroupLabelResolvers;
  entityLabelByKey?: ReadonlyMap<string, string>;
}): string => {
  if (groupKey === '') {
    return labels.emptyValue;
  }

  switch (groupByField) {
    case 'severity':
    case 'title':
    case 'classification':
    case 'cvss':
      return groupKey;
    case 'status':
      return formatFindingStatusForDisplay(groupKey);
    case 'category':
      return formatCategoryForDisplay(groupKey);
    case 'isPublished':
      return groupKey === FINDINGS_GROUP_PUBLISHED_KEY.PUBLISHED
        ? labels.published
        : labels.unpublished;
    case 'relatedEndpoint':
    case 'relatedSignal':
    case 'targetName':
    case 'linkedTicket':
      return entityLabelByKey?.get(groupKey) ?? groupKey;
    case 'createdAt':
    case 'publishedAt':
    case 'discoveryDate': {
      const date = new Date(`${groupKey}T00:00:00`);

      if (Number.isNaN(date.getTime())) {
        return labels.emptyValue;
      }

      return labels.formatDateDay(date);
    }
  }
};

const buildEntityLabelByKey = ({
  findings,
  groupByField,
  entityLabelByKey,
}: {
  findings: readonly FindingsGroupableFinding[];
  groupByField: FindingsGroupByField;
  entityLabelByKey?: ReadonlyMap<string, string>;
}): ReadonlyMap<string, string> => {
  const resolveLabel = FINDINGS_ENTITY_LABEL_BY_FIELD[groupByField];
  const labelsByKey = new Map(entityLabelByKey);

  if (!resolveLabel) {
    return labelsByKey;
  }

  for (const finding of findings) {
    const label = resolveLabel(finding);

    if (!label) {
      continue;
    }

    labelsByKey.set(getFindingsGroupKey({ finding, groupByField }), label);
  }

  return labelsByKey;
};

export const buildFindingsGroupedRows = <
  TFinding extends FindingsGroupableFinding,
>({
  findings,
  groupByField,
  collapsedGroupKeys,
  labels,
  serverCountByKey,
  knownGroupKeys,
  entityLabelByKey,
}: {
  findings: readonly TFinding[];
  groupByField: FindingsGroupByField;
  collapsedGroupKeys: ReadonlySet<string>;
  labels: FindingsGroupLabelResolvers;
  serverCountByKey?: ReadonlyMap<string, number>;
  knownGroupKeys?: readonly string[];
  entityLabelByKey?: ReadonlyMap<string, string>;
}): Array<TFinding | FindingsGroupHeaderRow> => {
  const resolvedEntityLabelByKey = buildEntityLabelByKey({
    findings,
    groupByField,
    entityLabelByKey,
  });

  return buildGroupedRows<TFinding, FindingsGroupHeaderRow>({
    items: findings,
    getItemId: (finding) => finding.id,
    getGroupKey: (finding) => getFindingsGroupKey({ finding, groupByField }),
    getGroupLabel: (groupKey) =>
      getFindingsGroupLabel({
        groupKey,
        groupByField,
        labels,
        entityLabelByKey: resolvedEntityLabelByKey,
      }),
    compareGroupKeys: (left, right) =>
      compareFindingsGroupKeys({
        left,
        right,
        groupByField,
        entityLabelByKey: resolvedEntityLabelByKey,
      }),
    collapsedGroupKeys,
    knownGroupKeys,
    headerIdPrefix: FINDINGS_GROUP_HEADER_ID_PREFIX,
    decorateHeader: (header, groupFindings) => ({
      ...header,
      count: Math.max(
        serverCountByKey?.get(header.groupKey) ?? 0,
        header.count,
      ),
      findingIds: header.itemIds,
      bulkActionableFindingIds: groupFindings
        .filter((finding) => !isArchivedLifecycle(finding.lifecycle))
        .map((finding) => finding.id),
    }),
  });
};
